import type { PermissionKey } from '@evimo/types/enums/permission-key';
import type { RoleResponseDto } from '@evimo/types/api/authorization/role/role.response.dto';
import type { RoleAuditUserResponseDto } from '@evimo/types/api/authorization/role/role-audit-user.response.dto';
import type { RoleDetailResponseDto } from '@evimo/types/api/authorization/role/role-detail.response.dto';
import type { RoleWithPermissionsResponseDto } from '@evimo/types/api/authorization/role/role-with-permissions.response.dto';
import type { RoleDeletionImpactResponseDto } from '@evimo/types/api/authorization/role/role-deletion-impact.response.dto';
import type { PaginationMetaDto } from '@evimo/types/api/pagination/pagination.response.dto';
import {
  authorizationRequestInit,
  rolesApi,
  systemRolesApi,
} from '~/app/services/authorization/authorization-api-instances';
import { invokeAuthorizationClient } from '~/app/services/authorization/authorization-api-error';
import {
  mapRoleDetailResponse,
  mapRoleWithPermissionsResponse,
} from '~/app/services/authorization/authorization-response.mappers';

export type RoleDto = RoleResponseDto;
export type RoleAuditUserDto = RoleAuditUserResponseDto;
export type RoleWithPermissionsDto = RoleWithPermissionsResponseDto;
export type RoleDetailDto = RoleDetailResponseDto;
export type RoleDeletionImpactDto = RoleDeletionImpactResponseDto;

export type ListRolesParams = {
  page?: number;
  limit?: number;
  q?: string;
};

export type CreateRolePayload = {
  name: string;
  permissionKeys?: PermissionKey[];
  cloneFromRoleId?: string;
};

export type SetRolePermissionsPayload = {
  permissionKeys: PermissionKey[];
};

function requireOrganizationId(organizationId: string): string {
  const normalized = organizationId.trim();
  if (!normalized) {
    throw new Error('Organization id is required');
  }
  return normalized;
}

export const RoleService = {
  async listRoles(
    organizationId: string,
    params: ListRolesParams = {},
  ): Promise<{
    data: RoleDto[];
    meta: PaginationMetaDto;
  }> {
    return invokeAuthorizationClient('Failed to fetch roles', () =>
      rolesApi.roleControllerListRoles(
        {
          organizationId: requireOrganizationId(organizationId),
          page: params.page ?? 1,
          limit: params.limit ?? 100,
          ...(params.q?.trim() ? { q: params.q.trim() } : {}),
        },
        authorizationRequestInit,
      ),
    );
  },

  async listRolesWithPermissions(
    organizationId: string,
  ): Promise<RoleWithPermissionsDto[]> {
    const response = await invokeAuthorizationClient(
      'Failed to fetch roles with permissions',
      () =>
        rolesApi.roleControllerListRolesWithPermissions(
          { organizationId: requireOrganizationId(organizationId) },
          authorizationRequestInit,
        ),
    );

    return response.data.map(mapRoleWithPermissionsResponse);
  },

  async findRoleById(
    organizationId: string,
    roleId: string,
  ): Promise<RoleDetailDto> {
    const role = await invokeAuthorizationClient('Failed to fetch role', () =>
      rolesApi.roleControllerFindRoleById(
        {
          organizationId: requireOrganizationId(organizationId),
          roleId: roleId.trim(),
        },
        authorizationRequestInit,
      ),
    );

    return mapRoleDetailResponse(role);
  },

  async findRoleDeletionImpact(
    organizationId: string,
    roleId: string,
  ): Promise<RoleDeletionImpactDto> {
    return invokeAuthorizationClient(
      'Failed to fetch role deletion impact',
      () =>
        rolesApi.roleControllerFindRoleDeletionImpact(
          {
            organizationId: requireOrganizationId(organizationId),
            roleId: roleId.trim(),
          },
          authorizationRequestInit,
        ),
    );
  },

  async createRole(
    organizationId: string,
    payload: CreateRolePayload,
  ): Promise<RoleDetailDto> {
    const role = await invokeAuthorizationClient('Failed to create role', () =>
      rolesApi.roleControllerCreateRole(
        {
          organizationId: requireOrganizationId(organizationId),
          createRoleRequestDto: payload as any,
        },
        authorizationRequestInit,
      ),
    );

    return mapRoleDetailResponse(role);
  },

  async updateRole(
    organizationId: string,
    roleId: string,
    name: string,
  ): Promise<RoleDetailDto> {
    const role = await invokeAuthorizationClient('Failed to update role', () =>
      rolesApi.roleControllerUpdateRole(
        {
          organizationId: requireOrganizationId(organizationId),
          roleId: roleId.trim(),
          updateRoleRequestDto: { name },
        },
        authorizationRequestInit,
      ),
    );

    return mapRoleDetailResponse(role);
  },

  async deleteRole(
    organizationId: string,
    roleId: string,
    fallbackRoleId?: string,
  ): Promise<void> {
    await invokeAuthorizationClient('Failed to delete role', () =>
      rolesApi.roleControllerDeleteRole(
        {
          organizationId: requireOrganizationId(organizationId),
          roleId: roleId.trim(),
          ...(fallbackRoleId?.trim()
            ? { fallbackRoleId: fallbackRoleId.trim() }
            : {}),
        },
        authorizationRequestInit,
      ),
    );
  },

  async setRolePermissions(
    organizationId: string,
    roleId: string,
    payload: SetRolePermissionsPayload,
  ): Promise<RoleDetailDto> {
    const role = await invokeAuthorizationClient(
      'Failed to update role permissions',
      () =>
        rolesApi.roleControllerSetRolePermissions(
          {
            organizationId: requireOrganizationId(organizationId),
            roleId: roleId.trim(),
            setRolePermissionsRequestDto: payload as any,
          },
          authorizationRequestInit,
        ),
    );

    return mapRoleDetailResponse(role);
  },

  async listSystemRoles(): Promise<RoleWithPermissionsDto[]> {
    const response = await invokeAuthorizationClient(
      'Failed to fetch system roles',
      () =>
        systemRolesApi.systemRoleControllerListSystemRoles(
          authorizationRequestInit,
        ),
    );

    return response.data.map(mapRoleWithPermissionsResponse);
  },

  async findSystemRoleById(roleId: string): Promise<RoleDetailDto> {
    const role = await invokeAuthorizationClient(
      'Failed to fetch system role',
      () =>
        systemRolesApi.systemRoleControllerFindSystemRoleById(
          { roleId: roleId.trim() },
          authorizationRequestInit,
        ),
    );

    return mapRoleDetailResponse(role);
  },

  async setSystemRolePermissions(
    roleId: string,
    payload: SetRolePermissionsPayload,
  ): Promise<RoleDetailDto> {
    const role = await invokeAuthorizationClient(
      'Failed to update system role permissions',
      () =>
        systemRolesApi.systemRoleControllerSetSystemRolePermissions(
          {
            roleId: roleId.trim(),
            setRolePermissionsRequestDto: payload as any,
          },
          authorizationRequestInit,
        ),
    );

    return mapRoleDetailResponse(role);
  },
};
